import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, IconButton, TextField } from '@mui/material';
import { Close as CloseIcon, Telegram as TelegramIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useFinancialContexts } from '../../contexts/ContextsContext';
import { transactionApi, Transaction } from '../../services/transactionApi';
import { BOT_URL } from '../../config/telegram';
import { formatMoneyCents } from '../../theme/chart';
import { tokens } from '../../theme/tokens';
import { useCompleteAccount } from './useCompleteAccount';

interface Props {
  /** The group in view, if any: the card speaks of it. */
  groupName?: string;
  /** The expense the person came to see, from the bot's "Ver meu painel". */
  transactionId?: string;
}

const dismissedKey = (userId: string) => `financy_guest_welcome_dismissed:${userId}`;

const readDismissed = (userId?: string): boolean => {
  if (!userId) return false;
  try {
    return localStorage.getItem(dismissedKey(userId)) === '1';
  } catch {
    return false;
  }
};

/** What the expense costs the person: their share, or all of it when they paid and nothing was split. */
const partOf = (transaction: Transaction, userId: string): number => {
  const shares = transaction.shares ?? [];
  if (shares.length) return Number(shares.find((share) => share.userId === userId)?.amount ?? 0);
  return (transaction.paidByUserId ?? transaction.userId) === userId ? Number(transaction.amount) : 0;
};

/**
 * Início for someone who came in from a Telegram group, with no email or
 * password yet: what they sent is right there, and so is the account, in
 * two fields. Once it is saved the card turns to their own, personal side:
 * the first thing of theirs to record, by Fin or here.
 */
export const GuestWelcomeCard: React.FC<Props> = ({ groupName, transactionId }) => {
  const { t, i18n } = useTranslation('auth');
  const navigate = useNavigate();
  const { state } = useAuth();
  const { selectContext } = useFinancialContexts();
  const { complete, error, isSaving } = useCompleteAccount();
  const user = state.user;
  const [dismissed, setDismissed] = useState(() => readDismissed(user?.id));
  const [justCompleted, setJustCompleted] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [expense, setExpense] = useState<Transaction | null>(null);

  useEffect(() => {
    if (!transactionId) return undefined;
    let cancelled = false;
    transactionApi
      .getTransaction(transactionId)
      .then((found) => {
        if (!cancelled) setExpense(found);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [transactionId]);

  if (!user || (!justCompleted && (!user.isGuest || dismissed))) return null;

  const dismiss = () => {
    if (!justCompleted) {
      try {
        localStorage.setItem(dismissedKey(user.id), '1');
      } catch {
        // Private mode: it shows again next time, nothing worse.
      }
    }
    setDismissed(true);
    setJustCompleted(false);
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (await complete(email, password)) setJustCompleted(true);
  };

  const addHere = () => {
    // Personal: an entry made from here goes into the group otherwise.
    selectContext(undefined);
    navigate('/transactions', { state: { newTransaction: true } });
  };

  const money = (value: number, currency: string) => formatMoneyCents(value, currency, i18n.language);
  const name = user.firstName ? user.firstName.charAt(0).toUpperCase() + user.firstName.slice(1) : '';

  return (
    <Box
      component='section'
      aria-label={t('guest.welcome.label')}
      sx={{ position: 'relative', bgcolor: tokens.accentSoft, borderRadius: '22px', p: { xs: 2.25, md: 3 }, pr: { xs: 6, md: 7 } }}
    >
      <IconButton aria-label={t('guest.welcome.close')} onClick={dismiss} sx={{ position: 'absolute', top: 8, right: 8, color: tokens.ink2 }}>
        <CloseIcon />
      </IconButton>

      {justCompleted ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxWidth: 640 }}>
          <Box component='h2' sx={{ m: 0, fontFamily: tokens.display, fontSize: { xs: 21, md: 24 }, fontWeight: 700 }}>
            {t('guest.welcome.doneTitle')}
          </Box>
          <Box component='p' sx={{ m: 0, color: tokens.ink2, lineHeight: 1.5 }}>{t('guest.welcome.doneText')}</Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
            <Button variant='contained' startIcon={<TelegramIcon />} href={BOT_URL} target='_blank' rel='noopener noreferrer' sx={{ minHeight: 44 }}>
              {t('guest.welcome.sendToFin')}
            </Button>
            <Button variant='outlined' onClick={addHere} sx={{ minHeight: 44, bgcolor: tokens.surface }}>
              {t('guest.welcome.addHere')}
            </Button>
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, md: 4 }, alignItems: 'flex-start' }}>
          <Box sx={{ flex: '1 1 320px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            <Box component='h2' sx={{ m: 0, fontFamily: tokens.display, fontSize: { xs: 21, md: 24 }, fontWeight: 700 }}>
              {name ? t('guest.welcome.title', { name }) : t('guest.welcome.titleNoName')}
            </Box>
            <Box component='p' sx={{ m: 0, color: tokens.ink2, lineHeight: 1.5 }}>
              {groupName ? t('guest.welcome.inGroup', { group: groupName }) : t('guest.welcome.general')}
            </Box>
            {expense && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, p: 1.5, borderRadius: '14px', bgcolor: tokens.surface }}>
                <Box component='span' sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: tokens.accent }}>
                  {t('guest.welcome.yourExpense')}
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, alignItems: 'baseline' }}>
                  <Box component='strong' sx={{ overflowWrap: 'anywhere' }}>{expense.description}</Box>
                  <Box component='strong' sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                    {money(Number(expense.amount), expense.currency)}
                  </Box>
                </Box>
                {expense.type === 'expense' && (
                  <Box component='span' sx={{ fontSize: 13, color: tokens.ink3 }}>
                    {t('guest.welcome.yourPart', { amount: money(partOf(expense, user.id), expense.currency) })}
                  </Box>
                )}
              </Box>
            )}
          </Box>

          <Box component='form' onSubmit={submit} noValidate sx={{ flex: '1 1 300px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {error && <Alert severity='error'>{error}</Alert>}
            <TextField
              label={t('guest.email')}
              type='email'
              autoComplete='email'
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              fullWidth
              sx={{ '& .MuiInputBase-root': { bgcolor: tokens.surface } }}
            />
            <TextField
              label={t('guest.password')}
              type='password'
              autoComplete='new-password'
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              helperText={t('guest.passwordHint')}
              fullWidth
              sx={{ '& .MuiInputBase-root': { bgcolor: tokens.surface } }}
            />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.25 }}>
              <Button type='submit' variant='contained' disabled={isSaving || !email || !password} sx={{ minHeight: 44 }}>
                {t('guest.welcome.submit')}
              </Button>
              <Button onClick={dismiss} sx={{ color: tokens.ink2 }}>{t('guest.welcome.later')}</Button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};
